import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { AccessibilityFeature, AccessibilityFeatureRow, ApiError, ManifestRow, VenueRow } from '../api';
import { endpoints } from '../api';
import { NotPermitted } from '../App';
import { GatesPanel } from './venue/GatesPanel';
import { VenueDetailsPanel } from './venue/DetailsPanel';
import { AccessibilityEditor } from '../components/AccessibilityEditor';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen, statusTone } from '../ui';

/** One venue and the manifests drawn for it. */
export default function VenuePage() {
  const { id = '' } = useParams();
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const venue = useQuery<
    { venue: VenueRow; manifests: ManifestRow[]; accessibility: { features: AccessibilityFeature[]; catalog: AccessibilityFeatureRow[] } },
    ApiError
  >({
    queryKey: ['venue', id],
    queryFn: () => endpoints.venue(id),
  });
  const saveAccess = useMutation({
    mutationFn: (input: { notes: string | null; features: { code: string; note: string | null }[] }) => endpoints.setVenueAccessibility(id, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['venue', id] }),
    onError: (cause: ApiError) => setError(cause.message),
  });

  if (!can('events.view')) return <NotPermitted permission="events.view" />;
  if (venue.error) return <ErrorNote>{venue.error.message}</ErrorNote>;

  const manifests = venue.data?.manifests ?? [];

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <div className="flex items-baseline gap-3">
        <Link to="/venues" className="text-sm text-accent underline-offset-2 hover:underline">
          ← Venues
        </Link>
        <h1 className="text-lg font-semibold text-ink-900">{venue.data?.venue.name ?? '…'}</h1>
        <span className="text-sm text-ink-400">{venue.data?.venue.timezone}</span>
        <Link to={`/venues/${id}/box-office`} className="ml-auto text-sm text-accent underline-offset-2 hover:underline">
          Box office setup →
        </Link>
      </div>

      {/*
        The address is what a customer navigates by (D-150), and this panel is
        shown to anyone who can open the venue — read-only without
        `venues.manage` rather than hidden (D-160).

        It used to be hidden, and an editor that vanishes when you lack a
        permission is indistinguishable from one that was never built: the first
        person to look for it reported it missing, on a deployment that had it.
      */}
      {venue.data ? (
        <VenueDetailsPanel
          venue={venue.data.venue}
          editable={can('venues.manage')}
          onSaved={() => {
            setError(null);
            void queryClient.invalidateQueries({ queryKey: ['venue', id] });
          }}
          onError={(cause) => setError(cause.message)}
        />
      ) : null}

      {adding ? (
        <NewManifest
          venueId={id}
          onCancel={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            setError(null);
            void queryClient.invalidateQueries({ queryKey: ['venue', id] });
          }}
          onError={(cause) => setError(cause.message)}
        />
      ) : null}

      <Panel
        title="Manifests"
        description="Seating layouts for this venue. Each is versioned: publishing freezes it, and re-drawing means a new version."
        actions={
          can('manifests.edit') && !adding ? (
            <Button variant="primary" onClick={() => setAdding(true)}>
              New manifest
            </Button>
          ) : null
        }
      >
        <Table headers={['Layout', 'Version', 'Status', 'Published', 'Created']}>
          {venue.isPending ? (
            <EmptyRow columns={5}>Loading…</EmptyRow>
          ) : manifests.length === 0 ? (
            <EmptyRow columns={5}>No layouts yet. Create one, draw its sections, then publish it.</EmptyRow>
          ) : (
            manifests.map((manifest) => (
              <tr key={manifest.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <Link
                    to={`/manifests/${manifest.id}`}
                    className="font-medium text-accent underline-offset-2 hover:underline"
                  >
                    {manifest.name}
                  </Link>
                  <div className="text-xs text-ink-400">{manifest.code}</div>
                </td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">v{manifest.version}</td>
                <td className="px-5 py-3">
                  <Badge tone={statusTone(manifest.status)}>{manifest.status}</Badge>
                </td>
                <td className="tabular px-5 py-3 text-xs text-ink-400">{formatWhen(manifest.published_at)}</td>
                <td className="tabular px-5 py-3 text-xs text-ink-400">{formatWhen(manifest.created_at)}</td>
              </tr>
            ))
          )}
        </Table>
      </Panel>

      {/* What the building offers, which is what the storefront says it offers (D-80). */}
      <AccessibilityEditor
        title="Accessibility"
        description="What this building offers. Every event here inherits these, and the event page shows exactly what is ticked — nothing is assumed."
        catalog={venue.data?.accessibility?.catalog ?? []}
        selected={venue.data?.accessibility?.features ?? []}
        notes={(venue.data?.venue as { accessibility_notes?: string | null } | undefined)?.accessibility_notes ?? null}
        editable={can('venues.manage')}
        saving={saveAccess.isPending}
        onSave={(draft) => saveAccess.mutate({ notes: draft.notes, features: draft.features })}
      />

      <GatesPanel venueId={id} canConfigure={can('scan.configure')} onError={(m) => setError(m)} />
    </div>
  );
}

function NewManifest({
  venueId,
  onCancel,
  onCreated,
  onError,
}: {
  venueId: string;
  onCancel: () => void;
  onCreated: () => void;
  onError: (error: ApiError) => void;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [width, setWidth] = useState('1200');
  const [height, setHeight] = useState('800');

  const create = useMutation({
    mutationFn: () =>
      endpoints.createManifest({
          venue_id: venueId,
          code: code.trim().toUpperCase(),
          name: name.trim(),
          canvas_width: Number(width),
          canvas_height: Number(height),
        }),
    onSuccess: onCreated,
    onError,
  });

  const valid = code.trim().length > 0 && name.trim().length > 0 && Number(width) > 0 && Number(height) > 0;

  return (
    <Panel
      title="New manifest"
      description="It starts as a draft. The canvas is the coordinate space the seat map is drawn in."
    >
      <form
        className="flex flex-wrap items-end gap-4 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Code" htmlFor="manifest-code" hint="Short and stable, e.g. AUDITORIUM-RESERVED">
          <input
            id="manifest-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="AUDITORIUM-RESERVED"
            className="w-60 rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </Field>

        <Field label="Name" htmlFor="manifest-name">
          <input
            id="manifest-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Reserved seating"
            className="w-56 rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </Field>

        <Field label="Canvas width" htmlFor="manifest-w">
          <input
            id="manifest-w"
            value={width}
            onChange={(event) => setWidth(event.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            className="tabular w-28 rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </Field>

        <Field label="Canvas height" htmlFor="manifest-h">
          <input
            id="manifest-h"
            value={height}
            onChange={(event) => setHeight(event.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            className="tabular w-28 rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </Field>

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={!valid || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create draft'}
          </Button>
          <Button type="button" onClick={onCancel} disabled={create.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}
